import { DevicePlatformType, UserPlatform } from '../common/types/common.types';
import { getMessagingToken, onForegroundMessage } from '../firebase/messaging';
import { upsertFcmToken } from '../http';

export interface NotificationData {
  id: string;
  title: string;
  body: string;
  ticketId?: string;
  timestamp: number;
  read: boolean;
}

export class NotificationService {
  static playNotificationSound(): void {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const gainNode = audioContext.createGain();
      gainNode.connect(audioContext.destination);

      const oscillator = audioContext.createOscillator();
      oscillator.connect(gainNode);
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.type = 'triangle';

      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.log('Audio not supported');
    }
  }

  static async requestPermission(): Promise<string | null> {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const token = await getMessagingToken();
        if (token) {
          const storedToken = localStorage.getItem('fcmToken');
          const tokenTimestamp = localStorage.getItem('fcmTokenTimestamp');
          const isTokenOld = !tokenTimestamp || Date.now() - parseInt(tokenTimestamp) > 7 * 24 * 60 * 60 * 1000;

          if (!storedToken || (storedToken !== token) || isTokenOld) {
            upsertFcmToken({ token, deviceId: navigator.userAgent, platform: UserPlatform.EM, platformType: DevicePlatformType.WEB })
              .then(() => {
                localStorage.setItem('fcmToken', token);
                localStorage.setItem('fcmTokenTimestamp', Date.now().toString());
              })
              .catch(error => console.error('Failed to sync FCM token:', error));
          }
        }
        return token;
      }
      return null;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return null;
    }
  }

  static setupForegroundListener(callback: (payload: any) => void) {
    return onForegroundMessage(callback);
  }

  static saveNotification(notification: NotificationData): void {
    const stored = localStorage.getItem('notifications');
    const notifications: NotificationData[] = stored ? JSON.parse(stored) : [];
    notifications.unshift(notification);
    localStorage.setItem('notifications', JSON.stringify(notifications.slice(0, 10)));

    // Play notification sound
    this.playNotificationSound();

    // Trigger shake animation
    this.triggerShakeAnimation();
  }

  static triggerShakeAnimation(): void {
    const notificationBtn = document.querySelector('[class*="notification_btn"]');
    if (notificationBtn) {
      notificationBtn.classList.add('shake');
      setTimeout(() => {
        notificationBtn.classList.remove('shake');
      }, 1000);
    }
  }

  static getStoredNotifications(): NotificationData[] {
    const stored = localStorage.getItem('notifications');
    return stored ? JSON.parse(stored) : [];
  }

  static markAsRead(notificationId: string): void {
    const stored = localStorage.getItem('notifications');
    if (stored) {
      const notifications: NotificationData[] = JSON.parse(stored);
      const updated = notifications.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      );
      localStorage.setItem('notifications', JSON.stringify(updated));
    }
  }
}